// @ts-strict-ignore
import { cents, money, percentage } from '../../util/formatters'
import { BulkAction, EventParentTypes, resolveKeywordMatchType } from '../../config'
import React from 'react'
import { auditTargetTypeLabel } from './audit-target-type'
import { resolveState } from '../../types/bulk_action_log'
import { capitalCase } from 'change-case'
import { ActionBadge } from '../actions2/Actions'
import { formatDateFromString } from '../../util/helpers'
import { format, parse, parseISO } from 'date-fns'
import { Link } from 'react-router-dom'
import { stripCommentsFromTrigger } from '../actions2/RecipesTable'
import { ActionsList, NumericalChangeActions } from '../../util/contstants'

export interface AuditLogI<TMeta extends AuditLogMeta = AuditLogMeta> {
    id: number
    profile_id: number
    target_type_id: number
    source_type_id: number
    source_id?: number
    meta: TMeta
    created_at: string
    updated_at: string
}

type AuditLogMeta =
    | SmartBidAuditLogMeta
    | PromotionAuditLogMeta
    | BulkActionAuditLogMeta
    | PublicApiAuditLogMeta
    | RecipeAuditLogMeta
    | ManualAuditLogMeta

interface SmartBidAuditLogMeta {
    count: number
}

interface PromotionAuditLogMeta {
    count: number
    counts: { [key: string]: number }
    // @todo other counts
    event: {
        promotion: any
    }
}

export interface BulkActionAuditLogMeta {
    count: number
    event: {
        filters: any
        query: string
        actions: any
        from: string
        to: string
    }
    successCount?: number
    failedCount?: number
}

interface PublicApiAuditLogMeta {
    count: number
    success_count?: number
    failed_count?: number
    event: {
        apiKeyId: string
        apiKeyName: string
        apiKeyPrefix: string
        mutationKind: 'update' | 'archive' | 'create'
        actions?: Record<string, unknown>
    }
}

interface RecipeAuditLogMeta {
    count: number
    event: {
        id: number
        ad_type: number
        ad_type_params?: any
        trigger: string
        action: number
        name: string
        action_params?: any
        frequency: number
        frequency_params?: any
        version?: number
    }
    negative_keyword_count?: number
    negative_keyword_error_count?: number
    negative_target_count?: number
    negative_target_error_count?: number
}

interface ManualAuditLogMeta {
    count: number
    event: {
        ad_type: number
        ad_type_params?: any
        trigger: string
        action: number
        action_params?: any
        frequency: number
        frequency_params?: any
    }
}

export type ValueFormatter = (any) => string

/**
 * Placement bid adjustments (MER-2785 / MER-2987). One bulk action carries one to
 * three of these keys, so every label path reads all of them rather than the first.
 */
const PLACEMENT_ACTION_LABELS: Record<string, string> = {
    tos_placement: 'Top of Search',
    ros_placement: 'Rest of Search',
    pp_placement: 'Product Pages',
}

/** Present placement keys, in the canonical display order. */
const placementActionKeys = (actions: Record<string, unknown>): string[] =>
    Object.keys(PLACEMENT_ACTION_LABELS).filter((key) => key in (actions ?? {}))

export class AuditLog<TMeta extends AuditLogMeta = AuditLogMeta> implements AuditLogI<TMeta> {
    id: number
    meta: TMeta
    profile_id: number
    source_id: number
    source_type_id: number
    target_type_id: number
    targets: number[]
    created_at: string
    updated_at: string
    _attribute: string
    _formatter: ValueFormatter

    constructor(auditLog: AuditLogI<TMeta>) {
        Object.assign(this, auditLog)
    }

    sourceInfo(): string | JSX.Element {
        switch (this.source_type_id) {
            case EventParentTypes.SmartBid:
                return ''
            case EventParentTypes.Promotion:
                return 'Smart Campaign' // @todo return Smart Campaign info
            case EventParentTypes.BulkAction:
                if ('event' in this.meta && 'from' in this.meta.event) {
                    return (
                        <div>
                            {this.meta.event.from && (
                                <div>
                                    From:{' '}
                                    {format(formatDateFromString(this.meta.event.from), 'M/d/yy')}
                                </div>
                            )}
                            {this.meta.event.to && (
                                <div>
                                    To: {format(formatDateFromString(this.meta.event.to), 'M/d/yy')}
                                </div>
                            )}
                            {this.meta.event.query && <div>Search: {this.meta.event.query}</div>}
                            {/* @todo filters*/}
                        </div>
                    )
                }
                console.warn('Cannot render Source for type:', this.source_type_id)
                return 'Something went wrong'
            case EventParentTypes.PublicApi:
                if ('event' in this.meta && 'apiKeyName' in this.meta.event) {
                    return (
                        <Link to="/api-keys">
                            {this.meta.event.apiKeyName} ({this.meta.event.apiKeyPrefix})
                        </Link>
                    )
                }
                console.warn('Cannot render Source for type:', this.source_type_id)
                return 'Something went wrong'
            case EventParentTypes.Recipe:
                if ('event' in this?.meta && 'name' in this?.meta?.event) {
                    return (
                        <div className="block max-w-[300px] overflow-hidden">
                            <div
                                className="truncate block"
                                title={stripCommentsFromTrigger(this.meta.event.trigger)}
                            >
                                Trigger: {stripCommentsFromTrigger(this.meta.event.trigger)}
                            </div>
                            <div className="flex">
                                Action:&nbsp;
                                <ActionBadge
                                    action={this.meta.event.action}
                                    action_params={this.meta.event.action_params}
                                />
                            </div>
                        </div>
                    )
                }
                console.warn('Cannot render Source for type:', this.source_type_id)
                return 'Something went wrong'
            default:
                console.warn('Cannot render Source for type:', this.source_type_id)
                return null
        }
    }

    sourceType(): string | null | JSX.Element {
        switch (this.source_type_id) {
            case EventParentTypes.SmartBid:
                return 'Smart Bids'
            case EventParentTypes.Promotion:
                return 'Smart Campaign' // @todo return Smart Campaign name
            case EventParentTypes.BulkAction:
                return 'Bulk Action'
            case EventParentTypes.PublicApi:
                return 'API'
            case EventParentTypes.Recipe:
                if ('event' in this?.meta && 'name' in this?.meta?.event) {
                    const recipeType = this.meta.event.version === 2 ? 'Segment' : 'Recipe'
                    return (
                        <Link to={`/recipes/${this.meta.event.id}`}>
                            {recipeType} - {this.meta.event.name}
                        </Link>
                    )
                }
                return 'Recipe' // @todo return recipe name
            default:
                console.warn('Cannot render Source for type:', this.source_type_id)
                return null
        }
    }

    targetType(): string | null {
        const targetType = auditTargetTypeLabel(this.target_type_id)
        if (targetType === null)
            console.warn('Cannot resolve target type for:', this.target_type_id)
        return targetType
    }

    effectedEntities(): string {
        if (
            Object.hasOwn(this.meta, 'event') &&
            // @ts-expect-error
            this.meta.event.hasOwnProperty('actions') &&
            // @ts-expect-error
            this.meta.event.actions.hasOwnProperty('bulk_action_failed')
        ) {
            return ``
        }
        return `${this.meta.count ?? 0} ${this.targetType().toLowerCase()}s`
    }

    event(): string | JSX.Element {
        switch (this.source_type_id) {
            case EventParentTypes.SmartBid:
                return 'Changed bids based on smart bid settings.'
            case EventParentTypes.Promotion:
                return this._resolvePromotionActionString(this.meta as PromotionAuditLogMeta)
            case EventParentTypes.BulkAction:
                return this._resolveBulkActionString(this.meta as BulkActionAuditLogMeta)
            case EventParentTypes.PublicApi:
                if ('event' in this.meta && 'actions' in this.meta.event) {
                    return this._resolveBulkActionString(this.meta as BulkActionAuditLogMeta)
                }
                return `${capitalCase((this.meta as PublicApiAuditLogMeta).event.mutationKind)} via API.`
            case EventParentTypes.Recipe:
                return this._resolveRecipeActionString(this.meta as RecipeAuditLogMeta)
            default:
                console.warn('Unresolved event for', this)
                return 'TODO'
        }
    }

    getAttribute(action?: string): string {
        if (action === undefined && this._attribute) {
            return this._attribute
        }

        const meta =
            action === undefined || !('event' in this.meta && 'actions' in this.meta.event)
                ? this.meta
                : {
                      ...this.meta,
                      event: { ...this.meta.event, actions: { [action]: true } },
                  }

        switch (this.source_type_id) {
            case EventParentTypes.SmartBid:
                this._attribute = 'bid'
                return this._attribute
            case EventParentTypes.Promotion:
                this._attribute = 'keyword'
                return this._attribute
            case EventParentTypes.BulkAction:
            case EventParentTypes.PublicApi:
                const attribute = this._resolveBulkActionAttribute(
                    this.target_type_id,
                    meta as BulkActionAuditLogMeta,
                )
                if (action !== undefined) return attribute
                this._attribute = attribute
                this._formatter = this._resolveBulkActionFormatter(
                    this.target_type_id,
                    meta as BulkActionAuditLogMeta,
                )
                return attribute
            case EventParentTypes.Recipe:
                this._attribute = this._resolveRecipeAttribute(this.meta as RecipeAuditLogMeta)
                return this._attribute
            default:
                console.warn('Unresolved mutatedKey for', this)
                return ''
        }
    }

    getFormatter(): ValueFormatter {
        if (this._formatter) {
            return this._formatter
        }

        this.getAttribute()

        if (!this._formatter) {
            console.warn('Could not find formatter for', this)
            return (v) => v
        }

        return this._formatter
    }

    isTargetAcosDelete(): boolean {
        return (
            'event' in this.meta &&
            'actions' in this.meta.event &&
            (this.meta.event.actions?.[12] === 'target_acos_delete' ||
                this.meta.event.actions?.target_acos === 'target_acos_delete')
        )
    }

    isSmartBidToggle(): boolean {
        return (
            'event' in this.meta &&
            'actions' in this.meta.event &&
            (this.meta.event.actions?.[19] ||
                typeof this.meta.event.actions?.smart_bid !== 'undefined')
        )
    }

    isRecipeToggle(): boolean {
        return (
            this.target_type_id === EventParentTypes.Recipe &&
            'event' in this.meta &&
            'actions' in this.meta.event &&
            typeof this.meta.event.actions.enabled !== 'undefined'
        )
    }

    isPromotionToggle(): boolean {
        return (
            this.target_type_id === EventParentTypes.Promotion &&
            'event' in this.meta &&
            'actions' in this.meta.event &&
            typeof this.meta.event.actions['promotions:enabled'] !== 'undefined'
        )
    }

    isPortfolioDelete(): boolean {
        return (
            'event' in this.meta &&
            'actions' in this.meta.event &&
            (this.meta.event.actions?.[14] || this.meta.event.actions?.portfolios_delete)
        )
    }

    isRecipeDelete(): boolean {
        return (
            this.target_type_id === EventParentTypes.Recipe &&
            'event' in this.meta &&
            'actions' in this.meta.event &&
            typeof this.meta.event.actions.recipes_delete !== 'undefined'
        )
    }

    isPromotionDelete(): boolean {
        return (
            this.target_type_id === EventParentTypes.Promotion &&
            'event' in this.meta &&
            'actions' in this.meta.event &&
            typeof this.meta.event.actions['promotions:delete'] !== 'undefined'
        )
    }

    isCreateProductAd(): boolean {
        return (
            this.target_type_id === EventParentTypes.AD_GROUP &&
            'event' in this.meta &&
            'actions' in this.meta.event &&
            typeof this.meta.event.actions['add_asin_to_ad_group'] !== 'undefined'
        )
    }

    _resolveBulkActionAttribute(target_type_id: number, meta: BulkActionAuditLogMeta): string {
        if (!('event' in meta && 'actions' in meta.event)) {
            console.log('Could not find event or actions in', meta)
            return ''
        }

        // A placement update carries one to three keys (MER-2987), so it cannot be
        // labelled from the first key alone.
        const placements = placementActionKeys(meta.event.actions)
        if (placements.length === 1) {
            return `${PLACEMENT_ACTION_LABELS[placements[0]]} bid adjustment`
        }
        if (placements.length > 1) {
            return 'bid adjustments'
        }

        const k = Object.keys(meta.event.actions)[0]
        const v = meta.event.actions[k]

        switch (this.target_type_id) {
            case EventParentTypes.Recipe:
                switch (k) {
                    case 'enabled':
                        return 'enabled'
                }
                break
            case EventParentTypes.Promotion:
                switch (k) {
                    case 'promotions:enabled':
                        return 'enabled'
                    case 'promotions:delete':
                        return 'Smart Campaign'
                }
                break
        }

        switch (k) {
            case BulkAction.ChangeState.toString():
            case 'state':
                return 'state'
            case BulkAction.ChangeDefaultBid.toString():
            case BulkAction.ChangeDefaultBidPercent.toString():
            case BulkAction.ChangeDefaultBidDelta.toString():
            case 'default_bid_percent':
            case 'default_bid_delta':
            case 'default_bid':
                return 'default bid'
            case BulkAction.ChangeDailyBudget.toString():
            case BulkAction.ChangeDailyBudgetPercent.toString():
            case BulkAction.ChangeDailyBudgetDelta.toString():
            case 'daily_budget_percent':
            case 'daily_budget_delta':
            case 'daily_budget':
                return 'daily budget'
            case BulkAction.ChangeBid.toString():
            case BulkAction.ChangeBidPercent.toString():
            case BulkAction.ChangeBidDelta.toString():
            case 'bid_percent':
            case 'bid_delta':
            case 'bid':
                return 'bid'
            case BulkAction.ChangeStartDate.toString():
            case 'start_date':
                return 'start date'
            case BulkAction.ChangeEndDate.toString():
            case 'end_date':
                return 'end date'
            case BulkAction.ChangeName.toString():
            case 'name':
                return 'name'
            case BulkAction.ChangePremiumBidAdjustment.toString():
            case 'premium_bid_adjustment':
                return 'premium bid adjustment'
            case BulkAction.ChangeTacos.toString():
            case 'target_acos':
                return 'target acos'
            case BulkAction.ChangeAssignedPortfolio.toString():
            case BulkAction.DeletePortfolio.toString():
            case 'portfolio':
            case 'portfolios_delete':
                return 'portfolio'
            case BulkAction.CreateNegativeKeyword.toString():
            case 'negative_keyword':
                return `negative ${resolveKeywordMatchType(v.match_type)} ${v.type}`
            case BulkAction.ChangeSmartBid.toString():
            case 'smart_bid':
                return 'smart bid'
            case 'bid_strategy':
                return 'bid strategy'
            case 'recipes_delete':
                return 'recipe'
            case 'asin':
                return 'product ad'
            case 'bulk_action_failed':
                return 'bulk action'
            default:
                console.warn('Cannot resolve bulk action attribute for', target_type_id, meta)
                return 'Bulk Action - todo'
        }
    }

    _resolveBulkActionFormatter(
        target_type_id: number,
        meta: BulkActionAuditLogMeta,
    ): ValueFormatter {
        if (!('event' in meta && 'actions' in meta.event)) {
            console.log('Could not find event or actions in', meta)
            return null
        }

        const k = Object.keys(meta.event.actions)[0]

        switch (this.target_type_id) {
            case EventParentTypes.Recipe:
                switch (k) {
                    case 'enabled':
                        return (v) => (v ? 'enabled' : 'disabled')
                    case 'recipes_delete':
                        return (v) => v
                }
                break
            case EventParentTypes.Promotion:
                switch (k) {
                    case 'promotions:enabled':
                        return (v) => (v ? 'enabled' : 'disabled')
                    case 'promotions:delete':
                        return (v) => v
                }
                break
        }

        switch (k) {
            case BulkAction.ChangeState.toString():
            case 'state':
                return (v) => resolveState(v)
            case BulkAction.ChangeDefaultBid.toString():
            case BulkAction.ChangeDailyBudget.toString():
            case BulkAction.ChangeBid.toString():
            case 'daily_budget':
            case 'default_bid':
            case 'bid':
                // The new rust bulk actions do _not_ store the metadata here in cents,
                // like the previous system did. So now we effectively have two versions
                // of the payload to contend with, one before the rust migration and one
                // after.
                const date2 = parseISO(this.created_at)
                const cutoff2 = new Date('2024-07-12T10:30:00Z')
                if (this.created_at) {
                    if (date2 < cutoff2) {
                        return cents
                    } else {
                        return money
                    }
                }
            case BulkAction.ChangeDefaultBidPercent.toString():
            case BulkAction.ChangeDailyBudgetPercent.toString():
            case BulkAction.ChangeBidPercent.toString():
            case 'daily_budget_percent':
            case 'default_bid_percent':
            case 'bid_percent':
                return (v) => percentage(Math.abs(v))
            case BulkAction.ChangeDefaultBidDelta.toString():
            case BulkAction.ChangeDailyBudgetDelta.toString():
            case BulkAction.ChangeBidDelta.toString():
            case 'daily_budget_delta':
            case 'default_bid_delta':
            case 'bid_delta':
                // The new rust bulk actions do _not_ store the metadata here in cents,
                // like the previous system did. So now we effectively have two versions
                // of the payload to contend with, one before the rust migration and one
                // after.
                const date = parseISO(this.created_at)
                const cutoff = new Date('2024-07-12T10:30:00Z')
                if (this.created_at) {
                    if (date < cutoff) {
                        return (v) => cents(Math.abs(v))
                    } else {
                        return (v) => money(Math.abs(v))
                    }
                }
            case BulkAction.ChangeStartDate.toString():
            case BulkAction.ChangeEndDate.toString():
            case 'start_date':
            case 'end_date':
                return (v) => v
            case BulkAction.ChangeName.toString():
            case 'name':
                return (v) => `"${v}"`
            case BulkAction.ChangeTacos.toString():
            case 'target_acos':
                return (v) => percentage(v / 100)
            case BulkAction.ChangePremiumBidAdjustment.toString():
            case BulkAction.ChangeAssignedPortfolio.toString():
            case BulkAction.DeletePortfolio.toString():
            case BulkAction.ChangeSmartBid.toString():
            case 'premium_bid_adjustment':
            case 'portfolio':
            case 'portfolios_delete':
            case 'smart_bid':
                return (v) => v
            case BulkAction.CreateNegativeKeyword.toString():
            case 'negative_keyword':
                return (v) =>
                    `negative ${resolveKeywordMatchType(v.match_type)} match ${v.type} "${
                        v.keyword
                    }"`
            case 'add_asin_to_ad_group':
                return (v) => v
            default:
                console.warn('Cannot resolve formatter for', this, this.meta, 'with', k)
                return (v) => v
        }
    }

    _resolveRecipeAttribute(meta: RecipeAuditLogMeta): string {
        if (!('event' in meta && 'action_params' in meta.event)) {
            console.log('Could not find event or action_params in', meta)
            return ''
        }

        const action = meta.event.action

        if (action === ActionsList.SetState) {
            this._formatter = resolveState
            return 'state'
        }

        switch (action) {
            case ActionsList.SetDailyBudget:
                return 'daily budget'
            case ActionsList.SetDefaultBid:
                return 'default bid'
            case ActionsList.SetBid:
                return 'bid'
            default:
                console.warn('Unhandled recipe action', meta)
                return ''
        }
    }

    _resolvePromotionActionString(meta: PromotionAuditLogMeta): string {
        if (!('counts' in meta)) {
            console.log('Could not find counts in', meta)
            return ''
        }

        const counts = {
            positiveKeyword: 0,
            positiveTarget: 0,
            negativeKeyword: 0,
            negativeTarget: 0,
        }

        Object.keys(meta.counts).forEach((k) => {
            if (k.indexOf('negative') !== -1) {
                if (k.indexOf('keyword') !== -1) {
                    counts.negativeKeyword += meta.counts[k]
                } else if (k.indexOf('target') !== -1) {
                    counts.negativeTarget += meta.counts[k]
                } else {
                    console.log('Unhandled key: ', k)
                }
            } else {
                if (k.indexOf('keyword') !== -1) {
                    counts.positiveKeyword += meta.counts[k]
                } else if (k.indexOf('target') !== -1) {
                    counts.positiveTarget += meta.counts[k]
                } else {
                    console.log('Unhandled key: ', k)
                }
            }
        })

        const parts = []
        Object.keys(counts).forEach((type) => {
            if (counts[type] > 0) {
                parts.push(
                    counts[type] +
                        ' ' +
                        capitalCase(type).toLowerCase() +
                        (counts[type] > 1 ? 's' : ''),
                )
            }
        })
        let partsString = parts.join(', ')
        if (parts.length > 1) {
            partsString = parts.slice(0, -1).join(', ') + ' and ' + parts.slice(-1)
        }

        return 'Created ' + partsString
    }

    _resolveBulkActionString(meta: BulkActionAuditLogMeta): string | JSX.Element {
        if (!('event' in meta && 'actions' in meta.event)) {
            console.log('Could not find event or actions in', meta)
            return ''
        }

        const placements = placementActionKeys(meta.event.actions)
        if (placements.length > 0) {
            // Every placement the run changed, not just the first key.
            const parts = placements.map(
                (key) => `${PLACEMENT_ACTION_LABELS[key]} to ${meta.event.actions[key]}%`,
            )
            const list =
                parts.length === 1
                    ? parts[0]
                    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
            return `Set ${list}`
        }

        const k = Object.keys(meta.event.actions)[0]
        const v = meta.event.actions[k]
        const dir =
            v > 0 ? (
                <span className="text-green-500">Increased</span>
            ) : (
                <span className="text-red-500">Decreased</span>
            )
        const attribute = this.getAttribute()

        if (this.target_type_id === EventParentTypes.Recipe) {
            switch (k) {
                case 'enabled':
                    return `${capitalCase(
                        this.getFormatter()(v),
                    )} ${this.targetType().toLowerCase()}`
                case 'recipes_delete':
                    return 'Deleted recipe'
            }
        } else if (this.target_type_id === EventParentTypes.Promotion) {
            switch (k) {
                case 'promotions:enabled':
                    return `${capitalCase(
                        this.getFormatter()(v),
                    )} ${this.targetType().toLowerCase()}`
                case 'promotions:delete':
                    return 'Deleted Smart Campaign'
            }
        }

        switch (k) {
            case BulkAction.ChangeState.toString():
            case 'state':
            case BulkAction.ChangeDefaultBid.toString():
            case BulkAction.ChangeDailyBudget.toString():
            case BulkAction.ChangeBid.toString():
            case BulkAction.ChangeStartDate.toString():
            case BulkAction.ChangeEndDate.toString():
            case BulkAction.ChangeName.toString():
            case 'daily_budget':
            case 'default_bid':
            case 'bid':
            case 'start_date':
            case 'end_date':
            case 'name':
                return `Set ${attribute} to ${this.getFormatter()(v)}`
            case BulkAction.ChangeTacos.toString():
            case 'target_acos':
                if (v === 'target_acos_delete') {
                    return 'Deleted target acos'
                }
                return `Set ${attribute} to ${this.getFormatter()(v)}`
            case BulkAction.ChangeDefaultBidPercent.toString():
            case BulkAction.ChangeDailyBudgetPercent.toString():
            case BulkAction.ChangeBidPercent.toString():
            case BulkAction.ChangeDefaultBidDelta.toString():
            case BulkAction.ChangeDailyBudgetDelta.toString():
            case BulkAction.ChangeBidDelta.toString():
            case 'daily_budget_percent':
            case 'default_bid_percent':
            case 'bid_percent':
            case 'daily_budget_delta':
            case 'default_bid_delta':
            case 'bid_delta':
                return (
                    <>
                        {dir} {attribute} by {this.getFormatter()(v)}
                    </>
                )
            case BulkAction.ChangePremiumBidAdjustment.toString():
            case 'premium_bid_adjustment':
                return `${v ? 'Enabled' : 'Disabled'} premium bid adjustment`
            case BulkAction.ChangeAssignedPortfolio.toString():
            case 'portfolio':
                return `Assigned to portfolio #${v}`
            case BulkAction.DeletePortfolio.toString():
            case 'portfolios_delete':
                return 'Deleted portfolio'
            case BulkAction.CreateNegativeKeyword.toString():
            case 'negative_keyword':
                return `Created negative ${resolveKeywordMatchType(v.match_type)} match ${
                    v.type
                } "${v.keyword}"`
            case BulkAction.ChangeSmartBid.toString():
            case 'smart_bid':
                return `${v ? 'Enabled' : 'Disabled'} smart bids`
            case 'bid_strategy':
                return `Set ${attribute} to ${v}`
            case 'asin':
                return `Created product ad with asin: ${v}`
            case BulkAction.AddAsinToAdGroup.toString():
            case 'add_asin_to_ad_group':
                console.log(meta)
                const m = `Created ${meta.successCount} product ads`
                if (meta.failedCount > 0) {
                    return (
                        <div>
                            {m}
                            <div>Failed creating {meta.failedCount}</div>
                        </div>
                    )
                }
                return m
            case 'bulk_action_failed':
                return 'Bulk Action Failed'
            default:
                console.warn('Cannot resolve bulk action string for', k)
                return 'Bulk Action - todo'
        }
    }

    _resolveRecipeActionString(meta: RecipeAuditLogMeta): string | JSX.Element {
        if (!('event' in meta && ('action_params' in meta.event || meta.event.action === 5))) {
            console.log('Could not find event or action_params in', meta)
            return ''
        }

        const action = meta.event.action
        const actionParams = meta.event.action_params
        const attribute = this.getAttribute()

        if (action === ActionsList.SetState) {
            return `Set ${attribute} to ${this.getFormatter()(actionParams.extra)}`
        }

        switch (action) {
            case ActionsList.SetDailyBudget:
            case ActionsList.SetDefaultBid:
            case ActionsList.SetBid:
                return this._resolveRecipeString(attribute, actionParams)
            case ActionsList.CreateExactMatchNegative:
                console.log('CreateExactMatchNegative', meta)
                let msg = ''
                if (meta?.negative_keyword_count > 0) {
                    msg = `Successfully created ${meta?.negative_keyword_count} exact match negative keywords`
                }
                if (meta?.negative_keyword_error_count > 0) {
                    if (msg) {
                        msg += ` and failed to create ${meta?.negative_keyword_error_count} exact match negative keywords`
                    } else {
                        msg += `Failed to create ${meta?.negative_keyword_error_count} exact match negative keywords`
                    }
                }
                if (meta?.negative_target_count > 0) {
                    if (msg) {
                        msg += ` and created `
                    } else {
                        msg += 'Successfully created '
                    }
                    msg += `${meta?.negative_target_count} negative product targets`
                }
                if (meta?.negative_target_error_count > 0) {
                    if (msg) {
                        msg += ` and failed to create ${meta?.negative_target_error_count} negative product targets`
                    } else {
                        msg += `Failed to create ${meta?.negative_target_error_count} negative product targets`
                    }
                }
                return msg
            default:
                console.warn('Unhandled recipe action', meta)
                return 'Recipe Action - todo'
        }
    }

    _resolveRecipeString(type: string, actionParams: any): JSX.Element {
        let dir
        let join = ''
        let divisor = 1
        const isVariable = actionParams.extra?.source === 'variable'

        switch (actionParams.extra.action) {
            case NumericalChangeActions.IncreasePercent:
                dir = <span className="text-green-500">Increased</span>
                join = 'by'
                this._formatter = (v) => percentage(v / 100)
                divisor = 100
                break
            case NumericalChangeActions.IncreaseDelta:
                dir = <span className="text-green-500">Increased</span>
                join = 'by'
                this._formatter = cents
                break
            case NumericalChangeActions.Set:
                dir = 'Set'
                join = 'to'
                this._formatter = cents
                break
            case NumericalChangeActions.DecreasePercent:
                dir = <span className="text-red-500">Decreased</span>
                join = 'by'
                this._formatter = (v) => percentage(v / 100)
                divisor = 100
                break
            case NumericalChangeActions.DecreaseDelta:
                dir = <span className="text-red-500">Decreased</span>
                join = 'by'
                this._formatter = cents
                break
        }

        const formattedValue = isVariable ? (
            <span className="text-xs font-mono border border-gray-300 bg-gray-100 rounded px-1 py-0.5">
                ${actionParams.extra.value}
            </span>
        ) : (
            this.getFormatter()(actionParams.extra.value)
        )

        return (
            <>
                {dir} {type} {join} {formattedValue}
            </>
        )
    }
}
